import FAB from '../UI/FAB';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Card, 
    Button, 
    Rate, 
    Tag, 
    Space, 
    Typography, 
    Row, 
    Col, 
    Input, 
    Select, 
    Slider, 
    Checkbox, 
    Divider,
    Avatar, 
    Badge, 
    Skeleton,
    Alert,
    Empty,
    Pagination,
    Tooltip,
    Drawer,
    Affix,
    message
} from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import EmptyState from '../UI/EmptyState';
import VendorCard from '../UI/VendorCard';
import FoodCard from '../UI/FoodCard';
import MarketplaceVendorCard from '../UI/MarketplaceVendorCard';
import MarketplaceGrid from '../UI/MarketplaceGrid';
import { resolveImageSrc, handleImageError } from '../../utils/images';

// Food decoration styles
const foodDecorationStyles = `
    .floating-food {
        position: absolute;
        opacity: 0.06;
        animation: float 7s ease-in-out infinite;
        pointer-events: none;
        z-index: 1;
    }
    
    .floating-food:nth-child(odd) {
        animation-delay: -2s;
        animation-duration: 9s;
    }
    
    .floating-food:nth-child(even) {
        animation-delay: -4s;
        animation-duration: 11s;
    }
    
    @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        25% { transform: translateY(-18px) rotate(4deg); }
        50% { transform: translateY(-9px) rotate(-2deg); }
        75% { transform: translateY(-14px) rotate(1deg); }
    }
    
    .food-bounce {
        animation: bounce 3.5s ease-in-out infinite;
    }
    
    @keyframes bounce {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-9px); }
    }
    
    .food-spin {
        animation: spin 22s linear infinite;
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    
    .food-pulse {
        animation: pulse 2.5s ease-in-out infinite;
    }
    
    @keyframes pulse {
        0%, 100% { opacity: 0.06; transform: scale(1); }
        50% { opacity: 0.12; transform: scale(1.08); }
    }
`;

// Food decoration component
const FoodDecoration = ({ emoji, className = "", style = {} }) => (
    <div className={`floating-food ${className}`} style={style}>
        <span className="text-2xl">{emoji}</span>
    </div>
);

import { 
    StarOutlined, 
    EnvironmentOutlined, 
    SearchOutlined,
    FilterOutlined,
    HeartOutlined,
    EyeOutlined,
    ThunderboltOutlined,
    CrownOutlined,
    FireOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    TeamOutlined,
    CarOutlined,
    GiftOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

function Vendors() {
    const [singleVendor, setSingleVendor] = useState(null);
    const [heroImg, setHeroImg] = useState('');
    useEffect(() => { try { setHeroImg(localStorage.getItem('hero_img_vendors') || ''); } catch (_) {} }, []);
    const navigate = useNavigate();
    const { user, requireAuth } = useAuth();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [useMarketplaceDesign, setUseMarketplaceDesign] = useState(true); // Toggle for new design
    const [cities, setCities] = useState([]);
    const [favorites, setFavorites] = useState(new Set());
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        city_id: '',
        cuisine: '',
        min_order: '',
        sort_by: 'rating',
        sort_order: 'desc'
    });
    const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });

    // Load persisted filters from localStorage and URL
    const loadPersistedFilters = () => {
        try {
            const savedFilters = localStorage.getItem('vendor_filters');
            const urlParams = new URLSearchParams(window.location.search);
            const selectedCity = localStorage.getItem('selected_city_id') || '';
            
            let persistedFilters = savedFilters ? JSON.parse(savedFilters) : {};
            
            // URL params take precedence over saved filters
            const urlFilters = {
                search: urlParams.get('search') || '',
                city_id: urlParams.get('city_id') || selectedCity || '',
                cuisine: urlParams.get('cuisine') || '',
                min_order: urlParams.get('min_order') || '',
                sort_by: urlParams.get('sort_by') || 'rating',
                sort_order: urlParams.get('sort_order') || 'desc'
            };
            
            // Merge persisted filters with URL filters (URL takes precedence)
            const mergedFilters = { ...persistedFilters, ...urlFilters };
            
            // Remove empty values
            Object.keys(mergedFilters).forEach(key => {
                if (mergedFilters[key] === '') {
                    delete mergedFilters[key];
                }
            });
            
            return { ...filters, ...mergedFilters };
        } catch (error) {
            console.error('Error loading persisted filters:', error);
            return filters;
        }
    };

    // Save filters to localStorage and update URL
    const persistFilters = (newFilters) => {
        try {
            // Save to localStorage
            const filtersToSave = { ...newFilters };
            // Remove empty values before saving
            Object.keys(filtersToSave).forEach(key => {
                if (filtersToSave[key] === '') {
                    delete filtersToSave[key];
                }
            });
            localStorage.setItem('vendor_filters', JSON.stringify(filtersToSave));
            
            // Update URL params
            const urlParams = new URLSearchParams();
            Object.keys(filtersToSave).forEach(key => {
                if (filtersToSave[key] !== '') {
                    urlParams.set(key, filtersToSave[key]);
                }
            });
            
            const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
            navigate(newUrl, { replace: true });
        } catch (error) {
            console.error('Error persisting filters:', error);
        }
    };

    // Load favorites from localStorage or API
    const loadFavorites = async () => {
        try {
            if (user) {
                // Load from API for logged-in users
                const response = await api.getVendorWishlist();
                if (response.success) {
                    setFavorites(new Set(response.data.map(item => item.vendor_id)));
                }
            } else {
                // Load from localStorage for guest users
                const savedFavorites = localStorage.getItem('vendor_favorites');
                if (savedFavorites) {
                    setFavorites(new Set(JSON.parse(savedFavorites)));
                }
            }
        } catch (error) {
            console.error('Error loading favorites:', error);
        }
    };

    // Save favorites to localStorage or API
    const persistFavorites = async (newFavorites) => {
        try {
            const favoritesArray = Array.from(newFavorites);
            if (user) {
                // Save to API for logged-in users
                await api.updateVendorWishlist(favoritesArray);
            } else {
                // Save to localStorage for guest users
                localStorage.setItem('vendor_favorites', JSON.stringify(favoritesArray));
            }
        } catch (error) {
            console.error('Error persisting favorites:', error);
        }
    };

    useEffect(() => {
        // Load persisted filters and favorites
        const persistedFilters = loadPersistedFilters();
        setFilters(persistedFilters);
        loadFavorites();
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadData();
    }, [filters, pagination.current, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    // Single-vendor mode: if only one vendor exists, redirect to its page so the site feels dedicated
    useEffect(() => {
        (async () => {
            try {
                const res = await api.getVendors({ per_page: 2 });
                const list = Array.isArray(res.data?.data) ? res.data.data : (res.data || []);
                if (Array.isArray(list) && list.length === 1) {
                    setSingleVendor(list[0]);
                    navigate(`/vendors/${list[0].id}`);
                }
            } catch {}
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Separate effect for persisting filters
    useEffect(() => {
        persistFilters(filters);
    }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            setLoading(true);
            const [citiesRes, vendorsRes] = await Promise.all([
                api.getCities(),
                api.getVendors({
                    ...filters,
                    page: pagination.current,
                    per_page: pagination.pageSize
                })
            ]);
            setCities(citiesRes.data);
            setVendors(vendorsRes.data.data || []);
            setPagination(prev => ({
                ...prev,
                total: vendorsRes.data.total || 0
            }));
        } catch (e) {
            setError('Failed to load vendors');
        } finally {
            setLoading(false);
        }
    };

    const toggleFavorite = async (vendorId) => {
        // Check if user is authenticated, save current filters and page state if not
        if (!requireAuth('toggle_favorite', {
            currentFilters: filters,
            currentPage: pagination.current_page,
            vendorId,
            action: 'toggle_favorite'
        })) {
            return;
        }
        
        setWishlistLoading(true);
        try {
            const newFavorites = new Set(favorites);
            let messageText;
            
            if (newFavorites.has(vendorId)) {
                newFavorites.delete(vendorId);
                messageText = 'Removed from favorites';
            } else {
                newFavorites.add(vendorId);
                messageText = 'Added to favorites';
            }
            
            setFavorites(newFavorites);
            await persistFavorites(newFavorites);
            message.success(messageText);
        } catch (error) {
            message.error('Failed to update favorites');
        } finally {
            setWishlistLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const handleSearch = (value) => {
        handleFilterChange('search', value);
    };

    const handleSort = (sortBy) => {
        const newOrder = filters.sort_by === sortBy && filters.sort_order === 'desc' ? 'asc' : 'desc';
        handleFilterChange('sort_by', sortBy);
        handleFilterChange('sort_order', newOrder);
    };

    const clearFilters = () => {
        const clearedFilters = {
            search: '',
            city_id: '',
            cuisine: '',
            min_order: '',
            sort_by: 'rating',
            sort_order: 'desc'
        };
        setFilters(clearedFilters);
        
        // Clear persisted filters
        localStorage.removeItem('vendor_filters');
        navigate(window.location.pathname, { replace: true });
    };

    const cuisines = [
        'North Indian', 'South Indian', 'Chinese', 'Continental', 
        'Italian', 'Mexican', 'Thai', 'Gujarati', 'Punjabi', 'Bengali'
    ];

    const renderVendorCard = (vendor) => {
        const isFavorite = favorites.has(vendor.id);
        const rating = Number(vendor.rating) || 0;
        const reviewCount = Number(vendor.total_reviews) || 0;

        return (
            <Col xs={24} sm={12} lg={8} xl={6} key={vendor.id}>
                <Card
                    hoverable
                    className="h-full group transition-all duration-300 card-compact card-ui"
                    cover={
                        <div className="relative overflow-hidden">
                            <div className="absolute inset-0 brand-gradient-2 flex items-center justify-center">
                                <span className="text-6xl">🍽️</span>
                            </div>
                            
                            {/* Overlay badges */}
                            <div className="absolute top-3 left-3 right-3 flex justify-between">
                                <div className="flex gap-2">
                                    {vendor.is_verified && (
                                        <Badge 
                                            count="Verified" 
                                            style={{ 
                                                backgroundColor: '#52c41a',
                                                fontSize: '10px',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                    <Tag color="gold" icon={<CrownOutlined />} className="text-xs">
                                        Premium
                                    </Tag>
                                </div>
                                <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                                    <Button
                                        type="text"
                                        icon={<HeartOutlined />}
                                        loading={wishlistLoading}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleFavorite(vendor.id);
                                        }}
                                        className={`text-white hover:text-red-500 ${
                                            isFavorite ? 'text-red-500' : ''
                                        }`}
                                    />
                                </Tooltip>
                            </div>
                            
                            {/* Price overlay hidden as per requirement; keep rating */}
                            <div className="absolute bottom-3 left-3 right-3">
                                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-2 flex items-center justify-end shadow-soft">
                                    <div className="text-right">
                                        <div className="flex items-center gap-1">
                                            <StarOutlined className="text-yellow-400 text-sm" />
                                            <Text className="font-semibold">{rating.toFixed(1)}</Text>
                                        </div>
                                        <Text type="secondary" className="text-xs">
                                            {reviewCount} reviews
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    actions={[
                        <Button 
                            type="text" 
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/vendors/${vendor.id}`)}
                            className="w-full"
                        >
                            View Details
                        </Button>,
                        <Button 
                            type="primary" 
                            onClick={() => navigate(`/booking?vendor_id=${vendor.id}`)}
                            className="w-full"
                        >
                            Book Now
                        </Button>
                    ]}
                >
                    <div className="p-1">
                        <div className="flex items-start justify-between mb-2">
                            <Title level={4} className="!mb-0 group-hover:text-orange-600 transition-colors line-clamp-1">
                                {vendor.business_name}
                            </Title>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-2">
                            <EnvironmentOutlined className="text-gray-400 text-sm" />
                            <Text type="secondary" className="text-sm">{vendor.city?.name}</Text>
                        </div>
                        
                        <Paragraph 
                            className="!mb-3 text-gray-600 line-clamp-2 text-sm"
                            ellipsis={{ rows: 2 }}
                        >
                            {vendor.description}
                        </Paragraph>
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                                <Rate disabled value={rating} size="small" className="text-yellow-400" />
                                <Text className="text-sm font-semibold">{rating.toFixed(1)}</Text>
                                <Text type="secondary" className="text-xs">({reviewCount})</Text>
                            </div>
                            <Tag color="green" className="text-xs">Available</Tag>
                        </div>
                    </div>
                </Card>
            </Col>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 relative overflow-hidden" data-emoji-context="photography">
            <style>{foodDecorationStyles}</style>
            {/* Food Decorations */}
            <FoodDecoration emoji="🍕" className="food-bounce" style={{ top: '8%', left: '4%' }} />
            <FoodDecoration emoji="🍔" className="food-spin" style={{ top: '18%', right: '6%' }} />
            <FoodDecoration emoji="🍜" className="food-pulse" style={{ top: '28%', left: '7%' }} />
            <FoodDecoration emoji="🍰" className="food-bounce" style={{ top: '38%', right: '9%' }} />
            <FoodDecoration emoji="🍱" className="food-spin" style={{ top: '48%', left: '3%' }} />
            <FoodDecoration emoji="🥘" className="food-pulse" style={{ top: '58%', right: '4%' }} />
            <FoodDecoration emoji="🍲" className="food-bounce" style={{ top: '68%', left: '9%' }} />
            <FoodDecoration emoji="🍳" className="food-spin" style={{ top: '78%', right: '7%' }} />
            <FoodDecoration emoji="🥗" className="food-pulse" style={{ top: '88%', left: '5%' }} />
            <FoodDecoration emoji="🍤" className="food-bounce" style={{ top: '98%', right: '8%' }} />
            
            {/* Hero Section - Modern Vibrant Design */}
            <div className="relative hero-modern text-white py-16 md:py-20">
                <div className="absolute inset-0 bg-black opacity-10"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Title level={1} className="!text-white !mb-4 !text-4xl sm:!text-5xl lg:!text-6xl font-extrabold tracking-tight">
                        Discover Amazing Caterers
                    </Title>
                    <Paragraph className="!text-white/90 !text-lg !mb-8 max-w-2xl mx-auto">
                        Find the perfect catering partner for your special events. 
                        Browse through our verified and trusted vendors.
                    </Paragraph>
                    {heroImg && (
                        <div className="mt-4"><img src={resolveImageSrc(heroImg)} alt="Vendors hero" className="mx-auto rounded-2xl shadow-lg max-h-56 object-cover" onError={handleImageError} /></div>
                    )}
                    
                    {/* Search Bar */}
                    <div className="max-w-2xl mx-auto">
                        <div className="flex items-center gap-4 mb-4">
                            <Search
                                placeholder="Search vendors, cuisines, or locations..."
                                size="large"
                                onSearch={handleSearch}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                value={filters.search}
                                className="rounded-xl flex-1"
                                style={{ borderRadius: '12px' }}
                            />
                            <Button
                                type={useMarketplaceDesign ? "primary" : "default"}
                                onClick={() => setUseMarketplaceDesign(!useMarketplaceDesign)}
                                size="large"
                                style={{ borderRadius: '8px' }}
                            >
                                {useMarketplaceDesign ? 'Modern View' : 'Classic View'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Row gutter={[24, 24]}>
                    {/* Filters Sidebar */}
                    <Col xs={24} lg={6}>
                        <div className="sticky top-4">
                            <Card title="Filters" className="shadow-lg border border-orange-100/50 modern-food-card" style={{ borderRadius: '20px' }}>
                                <div className="space-y-6">
                                    {/* City Filter */}
                                    <div>
                                        <Text strong className="block mb-2">City</Text>
                                        <Select
                                            placeholder="Select City"
                                            value={filters.city_id || undefined}
                                            onChange={(value) => handleFilterChange('city_id', value)}
                                            className="w-full"
                                            allowClear
                                        >
                                            {cities.map(city => (
                                                <Option key={city.id} value={city.id}>
                                                    {city.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </div>

                                    {/* Cuisine Filter */}
                                    <div>
                                        <Text strong className="block mb-2">Cuisine</Text>
                                        <Select
                                            placeholder="Select Cuisine"
                                            value={filters.cuisine || undefined}
                                            onChange={(value) => handleFilterChange('cuisine', value)}
                                            className="w-full"
                                            allowClear
                                        >
                                            {cuisines.map(cuisine => (
                                                <Option key={cuisine} value={cuisine}>
                                                    {cuisine}
                                                </Option>
                                            ))}
                                        </Select>
                                    </div>

                                    {/* Min Order Filter removed for user UI */}

                                    {/* Clear Filters */}
                                    <Button 
                                        onClick={clearFilters}
                                        className="w-full"
                                        type="dashed"
                                    >
                                        Clear All Filters
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    </Col>

                    {/* Results */}
                    <Col xs={24} lg={18}>
                        {/* Results Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                            <div>
                                <Title level={3} className="!mb-1 text-2xl font-bold text-gray-900">
                                    {loading ? 'Loading...' : `${pagination.total} Vendors Found`}
                                </Title>
                                <Text type="secondary">
                                    {filters.search && `Search results for "${filters.search}"`}
                                </Text>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <Text strong>Sort by:</Text>
                                <Select
                                    value={filters.sort_by}
                                    onChange={(value) => handleSort(value)}
                                    className="w-32"
                                >
                                    <Option value="rating">Rating</Option>
                                    <Option value="name">Name</Option>
                                </Select>
                                <Button
                                    icon={filters.sort_order === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
                                    onClick={() => handleFilterChange('sort_order', filters.sort_order === 'desc' ? 'asc' : 'desc')}
                                />
                            </div>
                        </div>

                        {/* Mobile Filter Button */}
                        <div className="lg:hidden mb-4">
                            <Button
                                icon={<FilterOutlined />}
                                onClick={() => setFilterDrawerVisible(true)}
                                className="w-full"
                            >
                                Filters
                            </Button>
                        </div>

                        {/* Vendors Grid */}
                        {loading ? (
                            <Row gutter={[16, 16]}>
                                {[...Array(8)].map((_, i) => (
                                    <Col xs={12} lg={6} key={i}>
                                        <div className="skeleton-card">
                                            <div className="h-32 w-full bg-slate-100 rounded-md mb-3"></div>
                                            <div className="space-y-2">
                                                <div className="skeleton-line w-3/4"></div>
                                                <div className="skeleton-line w-1/2"></div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        ) : error ? (
                            <Alert
                                message="Error"
                                description={error}
                                type="error"
                                showIcon
                            />
                        ) : vendors.length === 0 ? (
                            <EmptyState
                                title="No vendors found"
                                subtitle="Try a different city, cuisine, or clear filters."
                                action={<Button type="primary" onClick={clearFilters}>Clear Filters</Button>}
                                icon="🏪"
                            />
                        ) : (
                            <>
                                {useMarketplaceDesign ? (
                                    <MarketplaceGrid
                                        items={vendors}
                                        renderItem={(vendor) => (
                                            <MarketplaceVendorCard
                                                vendor={vendor}
                                                onViewDetails={() => navigate(`/vendors/${vendor.id}`)}
                                                onToggleFavorite={(vendor) => toggleFavorite(vendor.id)}
                                                isFavorite={favorites.has(vendor.id)}
                                                showFavorite={true}
                                                showStats={true}
                                                imageHeight={180}
                                            />
                                        )}
                                        loading={loading}
                                        emptyMessage="No vendors found"
                                        showPagination={false}
                                        showFilters={false}
                                        showSearch={false}
                                        showSort={false}
                                        gridProps={{
                                            xs: 24,
                                            sm: 12,
                                            md: 8,
                                            lg: 6,
                                            xl: 6
                                        }}
                                    />
                                ) : (
                                    <Row gutter={[16, 16]}>
                                        {vendors.map((vendor) => (
                                            <Col xs={24} sm={12} lg={8} xl={6} key={vendor.id}>
                                                <FoodCard
                                                    variant="vendor"
                                                    item={vendor}
                                                    imageHeight={160}
                                                    onView={() => navigate(`/vendors/${vendor.id}`)}
                                                    onFavorite={() => toggleFavorite(vendor.id)}
                                                    isFavorite={favorites.has(vendor.id)}
                                                    actionsPlacement="footer"
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                                
                                {/* Pagination */}
                                <div className="text-center mt-8">
                                    <Pagination
                                        current={pagination.current}
                                        pageSize={pagination.pageSize}
                                        total={pagination.total}
                                        onChange={(page, pageSize) => setPagination(prev => ({
                                            ...prev,
                                            current: page,
                                            pageSize: pageSize || prev.pageSize
                                        }))}
                                        showSizeChanger
                                        showQuickJumper
                                        showTotal={(total, range) => 
                                            `${range[0]}-${range[1]} of ${total} vendors`
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </div>

            {/* Mobile Filter Drawer */}
            <Drawer
                title="Filters"
                placement="right"
                onClose={() => setFilterDrawerVisible(false)}
                open={filterDrawerVisible}
                width={300}
            >
                <div className="space-y-6">
                    {/* City Filter */}
                    <div>
                        <Text strong className="block mb-2">City</Text>
                        <Select
                            placeholder="Select City"
                            value={filters.city_id || undefined}
                            onChange={(value) => handleFilterChange('city_id', value)}
                            className="w-full"
                            allowClear
                        >
                            {cities.map(city => (
                                <Option key={city.id} value={city.id}>
                                    {city.name}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* Cuisine Filter */}
                    <div>
                        <Text strong className="block mb-2">Cuisine</Text>
                        <Select
                            placeholder="Select Cuisine"
                            value={filters.cuisine || undefined}
                            onChange={(value) => handleFilterChange('cuisine', value)}
                            className="w-full"
                            allowClear
                        >
                            {cuisines.map(cuisine => (
                                <Option key={cuisine} value={cuisine}>
                                    {cuisine}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* Min Order Filter removed for user UI */}

                    {/* Clear Filters */}
                    <Button 
                        onClick={clearFilters}
                        className="w-full"
                        type="dashed"
                    >
                        Clear All Filters
                    </Button>
                </div>
            </Drawer>

        {/* Floating Action Button (Vendors) */}
        <FAB
            actions={[{
                label: 'Back to top',
                onClick: () => { try { document.querySelector('[data-emoji-context=photography]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch(_) { window.scrollTo({ top: 0, behavior: 'smooth' }); } },
                text: '↑'
            }]}
        />
        </div>
    );
}

export default Vendors;
